import { newUuid } from '@overlayqa/shared';
import { useRef, useState } from 'react';
import { AUTOMATION_EVENTS, parseAutomationDestination, toUserFacingMessage, type AutomationConnection, type AutomationDelivery, type AutomationEventType, type AutomationProvider } from '@overlayqa/shared';
import { useAuth } from '../../lib/auth';
import { useApiQuery } from '../../hooks/useApiQuery';
import { useProjects } from '../../hooks/useProjects';
import { automationsApi } from '../../lib/api/automations';
import { SelectDropdown } from '../shared/SelectDropdown';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { Checkbox } from '../ui/Checkbox';
import { WebhookIcon } from '@hugeicons/core-free-icons';
import { capture } from '@/lib/analytics';
import { ToolIntegrationCard } from './ToolIntegrationCard';
import { SetupDialog } from './SetupDialog';
import { BrandTile } from './integrationLogos';

const EVENT_LABELS: Record<AutomationEventType, string> = {
  'issue.created': 'Issue created', 'comment.created': 'Public comment added', 'issue.status_changed': 'Issue status changed',
};

/** Guide lines and links are the shared dialog's original Zapier and Make copy, split per tool (2026-10-18). */
const TOOLS: Record<AutomationProvider, { name: string; guide: string; help: string }> = {
  zapier: { name: 'Zapier', guide: 'Choose Webhooks by Zapier → Catch Hook, then copy its URL. Webhooks by Zapier requires a paid Zapier plan.',
    help: 'https://help.zapier.com/hc/en-us/articles/8496288690317-Trigger-Zaps-from-webhooks' },
  make: { name: 'Make', guide: 'Add Webhooks → Custom webhook, create a webhook, then copy its URL.', help: 'https://help.make.com/webhooks' },
};

function deliveryLabel(delivery?: AutomationDelivery): string {
  if (!delivery) return 'No deliveries yet';
  const labels = { pending: 'Queued', sending: 'Sending', accepted: 'Accepted by destination', failed: 'Delivery failed', cancelled: 'Cancelled' };
  return `${delivery.sample ? 'Sample: ' : ''}${labels[delivery.status]}`;
}

/** Zapier and Make cards in the /integrations grid; each opens the per-project setup filtered to that tool. */
export function AutomationIntegrations() {
  const availability = useApiQuery({ queryKey: ['automation-availability'], queryFn: automationsApi.availability });
  const [open, setOpen] = useState<AutomationProvider | null>(null);
  if (availability.isLoading || !availability.data?.enabled) return null;
  return <>
    {(['zapier', 'make'] as const).map(provider => <ToolIntegrationCard key={provider} provider={provider} mark={provider} name={TOOLS[provider].name} testId={`${provider}-card`}
      description={`Send project activity to ${TOOLS[provider].name} and let your existing workflow take it from there.`}
      onSetUp={() => {
        // Funnel step 1, the same event the provider cards' Connect sends.
        capture('integration_link_clicked', { provider, placement: 'integrations_page' });
        setOpen(provider);
      }} />)}
    {open && <AutomationSettings provider={open} onClose={() => setOpen(null)} />}
  </>;
}

function AutomationSettings({ provider, onClose }: { provider: AutomationProvider; onClose: () => void }) {
  const tool = TOOLS[provider];
  const other = TOOLS[provider === 'zapier' ? 'make' : 'zapier'].name;
  const { getToken } = useAuth();
  const projects = useProjects(undefined, { allTeams: true, includeCounts: false, includePreviews: false });
  const [projectId, setProjectId] = useState('');
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<AutomationEventType[]>([...AUTOMATION_EVENTS]);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [removing, setRemoving] = useState<AutomationConnection | null>(null);
  const createId = useRef<string | null>(null);
  const sampleIds = useRef<Record<string, string>>({});
  const saved = useApiQuery({ queryKey: ['automations', projectId], queryFn: token => automationsApi.list(token, projectId), enabled: !!projectId, refetchInterval: 10_000 });
  const canManage = saved.data?.canManage === true;
  const connections = saved.data?.connections.filter(c => c.provider === provider) ?? [];
  // A save in flight keeps the dialog open: reopening would mint a new create id and could duplicate the connection.
  const close = () => { if (!inFlight.current) onClose(); };

  async function act(operation: (token: string) => Promise<void>) {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true); setError(null); setNotice(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('Sign in again to manage this connection.');
      await operation(token);
      await saved.refetch();
    } catch (err) { setError(toUserFacingMessage(err, 'Could not save this change. Try again.')); }
    finally { inFlight.current = false; setBusy(false); }
  }

  return <SetupDialog title={tool.name} icon={WebhookIcon} mark={<BrandTile mark={provider} />} onClose={close} closeDisabled={busy}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <p className="modal-intro">Send project activity to {tool.name} and let your existing workflow take it from there.</p>
      <details className="text-sm text-gray-600">
        <summary className="cursor-pointer">Setup guide</summary>
        <div className="mt-3 space-y-2 max-w-lg">
          <p>{tool.guide}</p>
          <p>Paste that URL below, save, then send a sample while your automation is listening. Map the issue fields to your next action.</p>
          <p>Accepted means the destination received the request. Check {tool.name} to confirm the next action ran.</p>
          <p>Temporary failures retry automatically. Use event_id to prevent duplicate actions after a retry. Events can arrive out of order; occurred_at identifies when the change happened.</p>
          <a className="text-primary-600 underline" href={tool.help} target="_blank" rel="noopener noreferrer">{tool.name} instructions</a>
        </div>
      </details>
    </div>

    <div className="mt-4 space-y-2">
      <label htmlFor="automation-project" className="text-sm font-medium text-gray-700">Project</label>
      <SelectDropdown id="automation-project" aria-label="Automation project" value={projectId}
        options={(projects.data ?? []).filter(p => !p.isDemo && !p.archivedAt).map(p => ({ value: p.id, label: p.name }))}
        placeholder={projects.isLoading ? 'Loading projects...' : 'Choose a project'} disabled={busy || projects.isLoading}
        emptyLabel="No projects available" onChange={id => { setProjectId(id); setError(null); setNotice(null); setRemoving(null); createId.current = null; setName(''); setUrl(''); }} />
      {projects.isError && <p role="alert" className="text-sm text-red-600">Could not load projects. <button className="underline" onClick={() => void projects.refetch()}><span className="text-cap-trim block">Retry</span></button></p>}
    </div>

    {projectId && <div className="mt-4 space-y-4">
      {saved.isLoading && <p className="text-sm text-gray-500" role="status">Loading connections...</p>}
      {saved.isError && <p role="alert" className="text-sm text-red-600">Could not load connections. <button className="underline" onClick={() => void saved.refetch()}><span className="text-cap-trim block">Retry</span></button></p>}
      {saved.data && !canManage && <p className="text-sm text-gray-500">The project owner or a team administrator can manage these connections.</p>}
      {connections.map(connection => {
        const latest = saved.data?.deliveries.find(d => d.connectionId === connection.id);
        return <div key={connection.id} className="border-t border-gray-200 pt-4 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-sm font-medium text-gray-900 break-words">{connection.name}</h3>
            <p className="text-xs text-gray-500 mt-1">{connection.enabled ? 'Active' : 'Paused'}</p>
            <p className="text-xs text-gray-500 mt-1">{connection.events.map(e => EVENT_LABELS[e]).join(' · ')}</p>
            <p className="text-sm text-gray-600 mt-2" role="status">{deliveryLabel(latest)}</p>
            {latest?.error && <p className="text-xs text-red-600 mt-1">{latest.error}</p>}
            {latest && <p className="text-xs text-gray-400 mt-1">{new Date(latest.createdAt).toLocaleString()}</p>}
          </div>
          {canManage && <div className="flex flex-wrap gap-2">
            <button className="btn-secondary text-sm" disabled={busy || !connection.enabled} onClick={() => void act(async token => {
              const sampleId = sampleIds.current[connection.id] ??= newUuid();
              await automationsApi.sample(token, projectId, connection.id, sampleId);
              delete sampleIds.current[connection.id];
              setNotice(`Sample queued. Check your listening automation in ${tool.name}.`);
            })}><span className="text-cap-trim">Send sample</span></button>
            <button className="btn-secondary text-sm" disabled={busy} onClick={() => void act(async token => {
              await automationsApi.setEnabled(token, projectId, connection.id, !connection.enabled);
              setNotice(connection.enabled ? 'Connection paused. Queued deliveries were cancelled; a request already in flight may still arrive.' : 'Connection resumed. New activity will be sent.');
            })}><span className="text-cap-trim">{connection.enabled ? 'Pause' : 'Resume'}</span></button>
            <button className="btn-secondary text-sm" disabled={busy} onClick={() => { setError(null); setRemoving(connection); }}><span className="text-cap-trim">Remove</span></button>
          </div>}
        </div>;
      })}
      {saved.data && connections.length === 0 && <p className="text-sm text-gray-500">No {tool.name} connections for this project yet.</p>}
      {canManage && <form className="border-t border-gray-200 pt-4 space-y-3" onSubmit={e => {
        e.preventDefault();
        // Same rule the server saves by: a URL for the other tool would save under that tool and vanish from this list.
        let detected: AutomationProvider | null = null;
        try { detected = parseAutomationDestination(url).provider; } catch { detected = null; /* malformed: the server returns its own message */ }
        if (detected && detected !== provider) { setNotice(null); setError(`That is a ${other} webhook URL. Add it from the ${other} card.`); return; }
        void act(async token => {
          const id = createId.current ??= newUuid();
          await automationsApi.create(token, projectId, { id, name, url, events });
          createId.current = null; setName(''); setUrl('');
          setNotice('Connection saved. Send a sample to finish setting up your automation.');
        });
      }}>
        <h3 className="text-sm font-medium text-gray-900">Add a connection</h3>
        <div><label className="block text-sm text-gray-700 mb-1" htmlFor="automation-name">Connection name</label>
          <input id="automation-name" className="input w-full" value={name} maxLength={80} required disabled={busy} placeholder="Client feedback workflow" onChange={e => setName(e.target.value)} /></div>
        <div><label className="block text-sm text-gray-700 mb-1" htmlFor="automation-url">Webhook URL</label>
          <input id="automation-url" className="input w-full" type="url" value={url} maxLength={2048} autoComplete="off" required disabled={busy} placeholder={`Paste your ${tool.name} webhook URL`} onChange={e => { setUrl(e.target.value); createId.current = null; }} />
          <p className="text-xs text-gray-500 mt-1">Stored securely. The saved URL is never shown to other project members.</p></div>
        <fieldset disabled={busy} className="space-y-2">
          <legend className="text-sm text-gray-700 mb-2">Send when</legend>
          {AUTOMATION_EVENTS.map(event => <label key={event} className="flex gap-2 items-center text-sm text-gray-700">
            <Checkbox checked={events.includes(event)} onChange={e => setEvents(current => e.target.checked ? [...current, event] : current.filter(v => v !== event))} />{EVENT_LABELS[event]}
          </label>)}
        </fieldset>
        <p className="text-xs text-gray-500">New issues are sent when saved. Internal comments stay in OverlayQA.</p>
        <button className="btn-primary text-sm" disabled={busy || events.length === 0}><span className="text-cap-trim">{busy ? 'Saving...' : 'Save connection'}</span></button>
      </form>}
    </div>}
    {error && !removing && <p role="alert" className="text-sm text-red-600 mt-3">{error}</p>}
    {notice && <p role="status" className="text-sm text-gray-600 mt-3">{notice}</p>}
    <ConfirmDialog open={!!removing} title="Remove automation?" message="This stops future deliveries and removes this connection’s history. A request already in flight may still arrive." confirmLabel="Remove" destructive loading={busy} error={error}
      onCancel={() => { if (!busy) { setRemoving(null); setError(null); } }} onConfirm={() => { if (removing) void act(async token => {
        await automationsApi.remove(token, projectId, removing.id); setRemoving(null); setNotice('Connection removed.');
      }); }} />
  </SetupDialog>;
}
